import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  IconButton, 
  TextField, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Checkbox, 
  Drawer,
  Link
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Close as CloseIcon } from '@mui/icons-material';

const AadhaarDemographicAuth = ({ onBack, onVerify, aadhaarNumber }) => {
  const [name, setName] = useState('');
  const [gender, setGender] = useState('2'); // Default to Female as in the snippet
  const [yob, setYob] = useState('');
  const [consent, setConsent] = useState(false);
  const [isConsentOpen, setIsConsentOpen] = useState(false);

  // Format Aadhaar Number to display as XXXX-XXXX-XXXX
  const formattedAadhaar = aadhaarNumber 
    ? aadhaarNumber.replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3')
    : 'XXXX-XXXX-XXXX';

  const handleVerify = () => {
    const payload = {
      aadhaarNumber,
      name,
      gender,
      yob,
      consent
    };
    console.log("Demographic Auth Payload:", payload);
    if (onVerify) {
      onVerify(payload);
    }
  };

  const isFormValid = name.trim() !== '' && gender !== '' && yob.length === 4 && consent;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">Demographic Authentication</Typography>
      </Box>

      <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* UID Number */}
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={500} gutterBottom>
            UID Number
          </Typography>
          <Typography variant="h6" fontWeight="bold">
            {formattedAadhaar}
          </Typography>
        </Box>

        {/* Name */}
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', '&::before': { content: '"*"', color: 'error.main', mr: 0.5 } }} fontWeight={500} gutterBottom>
            Name
          </Typography>
          <TextField 
            fullWidth 
            variant="outlined" 
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Box>

        {/* Gender */}
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', '&::before': { content: '"*"', color: 'error.main', mr: 0.5 } }} fontWeight={500} gutterBottom>
            Gender
          </Typography>
          <RadioGroup 
            row 
            value={gender} 
            onChange={(e) => setGender(e.target.value)}
          >
            <FormControlLabel value="1" control={<Radio color="primary" />} label="Male" />
            <FormControlLabel value="2" control={<Radio color="primary" />} label="Female" />
            <FormControlLabel value="3" control={<Radio color="primary" />} label="Others" />
          </RadioGroup>
        </Box>

        {/* Year of Birth */}
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', '&::before': { content: '"*"', color: 'error.main', mr: 0.5 } }} fontWeight={500} gutterBottom>
            Year of Birth
          </Typography>
          <TextField 
            fullWidth 
            type="number"
            variant="outlined" 
            size="small"
            value={yob}
            onChange={(e) => {
              const val = e.target.value;
              if (val.length <= 4) setYob(val);
            }}
          />
        </Box>

        {/* Consent Checkbox */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1 }}>
          <Checkbox 
            checked={consent} 
            onChange={(e) => setConsent(e.target.checked)} 
            color="primary"
            sx={{ pt: 0, pl: 0 }}
          />
          <Typography variant="body2" color="text.primary">
            I hereby confirm that I have read out the information provided by the beneficiary...{' '}
            <Link 
              component="button" 
              variant="body2" 
              onClick={(e) => {
                e.preventDefault();
                setIsConsentOpen(true);
              }}
              sx={{ fontWeight: 'bold', textDecoration: 'none', cursor: 'pointer', verticalAlign: 'baseline' }}
            >
              Read More
            </Link>
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
        <Button 
          variant="contained" 
          onClick={handleVerify} 
          disabled={!isFormValid}
          sx={{ borderRadius: 3, px: 4, py: 1.5, textTransform: "none" }}
        >
          Verify Member
        </Button>
      </Box>

      {/* Aadhaar Consent Drawer/Bottom Sheet */}
      <Drawer
        anchor="bottom"
        open={isConsentOpen}
        onClose={() => setIsConsentOpen(false)}
        PaperProps={{
          sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, pb: 4, pt: 2, px: 2, maxHeight: '80vh' }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={() => setIsConsentOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 2, overflowY: 'auto' }}>
          <Typography variant="body1" paragraph>
            I, hereby declare that I am voluntarily sharing my Aadhaar Number / Virtual ID issued by UIDAI and demographic information, with National Health Authority (NHA) for the sole purpose of creation of an Ayushman Bharat Health Account (ABHA (number)).
          </Typography>
          <Typography variant="body1" paragraph>
            I understand that my ABHA (number) can be used and shared for purposes as may be notified by Ayushman Bharat Digital Mission (ABDM) from time to time including provision of healthcare services. Further, I am aware that my personal identifiable information (Name, Address, Age, Date of Birth, Gender and Photograph) may be made available to the entities working in the National Digital Health Ecosystem (NDHE) which inter alia may include stakeholders and entities such as healthcare professionals (e.g. doctors), facilities (e.g. hospitals, laboratories) and data fiduciaries (e.g. health programmes), which are registered with or linked to the Ayushman Bharat Digital Mission (ABDM), and various processes there under.
          </Typography>
          <Typography variant="body1">
            I authorize NHA to use my Aadhaar number / Virtual ID for performing Aadhaar based authentication with UIDAI as per the provisions of the Aadhaar (Targeted Delivery of Financial and other Subsidies, Benefits and Services) Act, 2016 for the aforesaid purpose. I understand that UIDAI will share my e-KYC details, or response of "Yes" with NHA upon successful authentication.
          </Typography>
        </Box>
      </Drawer>
    </Box>
  );
};

export default AadhaarDemographicAuth;
